import React, { useEffect, useMemo, useRef } from 'react';
import { debounce, type DebouncedFunc } from 'lodash-es';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import type { FixedLengthArray } from '@utils/types';

type ReturnValue = FixedLengthArray<
  [
    {
      isScrolledToTop: boolean;
      isScrolledToBottom: boolean;
    },
    {
      onScroll: DebouncedFunc<(_e: React.UIEvent<HTMLDivElement>) => void>;
      ref: React.RefObject<HTMLDivElement>;
    },
  ]
>;

export const useScrollPosition = (): ReturnValue => {
  const [isScrolledToTop, setIsScrolledToTop] = React.useState<boolean>(true);
  const [isScrolledToBottom, setIsScrolledToBottom] = React.useState<boolean>(true);
  const ref = useRef<HTMLDivElement>(null);

  const handleChange = useStableCallback(() => {
    const element = ref.current;
    if (!element) return;
    if (!element?.scrollHeight) return setIsScrolledToTop(true);
    setIsScrolledToTop(element.scrollTop === 0);
    // Use a small margin to avoid bounce delaying isScrolledToTop to return true
    setIsScrolledToBottom(element.scrollHeight - element.scrollTop <= element.clientHeight);
  });

  const handleScroll = useStableCallback((_e: React.UIEvent<HTMLDivElement>) => {
    handleChange();
  });

  const debouncedHandleScroll = useMemo(() => {
    return debounce(handleScroll, 100, {
      leading: true,
      trailing: true,
    });
  }, [handleScroll]);

  /**
   * Watch for content size changes (children added or removed)
   */
  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new MutationObserver((_mutationList, _observer) => {
      handleChange();
    });
    observer.observe(element, { childList: true });

    return () => {
      observer.disconnect();
    };
  }, [ref, handleChange]);

  const memoizedValues = useMemo<ReturnValue>(() => {
    return [
      { isScrolledToTop, isScrolledToBottom },
      {
        onScroll: debouncedHandleScroll,
        ref: ref,
      },
    ];
  }, [isScrolledToTop, isScrolledToBottom, debouncedHandleScroll, ref]);

  return memoizedValues;
};
